// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE BORROWER'S FIVE STEPS AFTER THE APPLICATION (Jesse/Hannah, 2026-11-02 — the Home Shopping page redesign).
// Replaces the 8-step tracker (AppShell ProgressBar) on every borrower page: "Step X of 5", the step name and the
// 5-segment bar. DISPLAY ONLY — each step groups stages that already exist (backend constants/stages.js); no stage is
// added or renamed, and nothing changes for DoorlyPro, Eva or notifications.
// Pure — no imports — so backend/test/journeySteps.test.js runs it.
//
//   1 Home search      home_shopping; approved / conditional_approval (decided, not yet signed); not_moving_forward
//   2 Offer            offer_requested, offer_submitted, offer_accepted (requested → sent → ACCEPTED)
//   3 Under contract   option_period, under_contract (new construction) — reached only when Doorly has signed the A-B
//                      contract (backend utils/abPsaExecuted; finalizeAbPsa moves offer_accepted on at that moment)
//   4 Closing          loan_docs, closing_disclosures, final_review, closing, clear_to_close
//   5 Keys in hand     closed, funded
// 🔑 THE STAGE MIX-UP FIXED (dashboard stages spec A, Jesse/Hannah 2026-11-03): offer_accepted was step 3. At "accepted"
// the borrower reviews the Loan Estimate and completes the offer-accepted review; the bar stays on Offer. Step names =
// Set 1 ("Home search · Offer · Under contract · Closing · Keys in hand").
//   no header          every stage before the decision (the guided application has its own steps), on_hold, adverse,
//                      adverse_pending; an archived, adversed or under-review file
// The four stages the old tracker had no slot for (conditional_approval, offer_submitted, clear_to_close, funded) are
// covered.
// ─────────────────────────────────────────────────────────────────────────────

export const JOURNEY_STEP_NAMES = ['Home search', 'Offer', 'Under contract', 'Closing', 'Keys in hand'];
export const JOURNEY_TOTAL = 5;

export const JOURNEY_STAGE_STEP = {
  home_shopping: 1, approved: 1, conditional_approval: 1, not_moving_forward: 1,
  offer_requested: 2, offer_submitted: 2, offer_accepted: 2,
  option_period: 3, under_contract: 3,
  loan_docs: 4, closing_disclosures: 4, final_review: 4, closing: 4, clear_to_close: 4,
  closed: 5, funded: 5,
};

/** { step, name, total } for a /api/borrower/status answer, or null (no header). */
export function journeyStepFor(status) {
  if (!status || !status.loan_stage) return null;
  if (status.under_review === true || status.is_adversed || status.is_archived) return null;
  const step = JOURNEY_STAGE_STEP[String(status.loan_stage)];
  if (!step) return null;
  return { step, name: JOURNEY_STEP_NAMES[step - 1], total: JOURNEY_TOTAL };
}

// ── The main card's wording (approved, curly apostrophes) ──
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ymdParts = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
};
/** 'YYYY-MM-DD' → "Nov 9" (the calendar date as stored, never shifted by a zone). */
export function formatMonD(ymd) {
  const p = ymdParts(ymd);
  return p ? `${MONTHS[p.mo - 1]} ${p.d}` : '';
}
/** 'YYYY-MM-DD' → "Monday, Dec 7". */
export function formatWeekdayMonD(ymd) {
  const p = ymdParts(ymd);
  if (!p) return '';
  return `${WEEKDAYS[new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay()]}, ${MONTHS[p.mo - 1]} ${p.d}`;
}
export const formatUsd = (n) => (Number(n) > 0 ? `$${Math.round(Number(n)).toLocaleString('en-US')}` : '');

export const JOURNEY_COPY = {
  stepLine: (n) => `Step ${n} of ${JOURNEY_TOTAL}`,
  stepLinePhone: (n, name) => `Step ${n} of ${JOURNEY_TOTAL} · ${name}`,
  welcome: (first) => (first ? `Welcome back, ${first}` : 'Welcome back'),
  approved: (amount) => `You’re approved up to ${formatUsd(amount)}`,
  browse: 'Browse homes',
  viewSmartEstimate: 'View your SmartEstimate',
  // Today's next-step sentence and item 10's approved sentence (Hannah's review, 2026-11-02; "down payment" plain text).
  homeShoppingNext: 'Browse and find the right home. Once you request an offer, your Doorly team takes it from there.',
  downPaymentReady: 'When you’re ready to make an offer, have your down payment ready. Doorly buys the home with cash first, so we need your down payment confirmed before we commit funds to the purchase.',
  underContract: (address, price) => ['Under contract', address, formatUsd(price)].filter(Boolean).join(' · '),
  optionEnds: (ymd) => `Your option period ends ${formatMonD(ymd)} at 5:00 p.m.`,
  closingTitle: (address, price) => ['Closing', address, formatUsd(price)].filter(Boolean).join(' · '),
  closingOn: (ymd) => `Your closing is on ${formatWeekdayMonD(ymd)}.`,
  questions: 'Chat with us anytime, or visit the Help Center.',

  // ── 🔑 THE STAGE PAGES (dashboard stages spec, Jesse/Hannah 2026-11-03 — wording APPROVED in the spec, used exactly) ──
  // Offer · requested. "1 business day" is NOT what staff tooling enforces (no SLA exists for sending an offer; the
  // Home detail page already promises only "shortly"), so the line says "shortly", as the spec directs.
  offerRequested: (street) => (street ? `We’re preparing Doorly’s offer on ${street}` : 'We’re preparing your offer'),
  offerRequestedLine: 'We’ll send it to the seller’s agent shortly and let you know as soon as it’s out.',
  // Offer · sent
  offerSent: (street) => (street ? `Doorly’s offer on ${street} has been sent` : ''),
  offerSentLine: 'It’s with the seller’s agent. We’ll let you know as soon as we hear back.',
  messageTeam: 'Message your Doorly team',
  // Offer · accepted
  offerAccepted: (street) => (street ? `Doorly’s offer on ${street} was accepted` : ''),
  offerAcceptedLine: 'Review your Loan Estimate and confirm your next step so Doorly can sign the purchase contract.',
  offerAcceptedNote: 'This takes about 2–3 minutes.',
  reviewContinue: 'Review & continue',
  continueReview: 'Continue review',
  saveForLater: 'Save for later',
  dontMoveForward: 'I don’t want to move forward with this home',
  offerAcceptedOn: 'Offer accepted',
  // Under contract — the checklist (borrower to-dos only; Doorly's tasks are status lines)
  signPurchaseContract: 'Sign your purchase contract',
  sendDownPayment: 'Send your down payment',
  appraisalStatus: 'Appraisal: Doorly orders it once your contract is signed and your down payment arrives.',
  reviewAppraisal: 'Review appraisal package',
  viewWireInstructions: 'View wire instructions',
  sentMyWire: 'I’ve sent my wire',
  wireCallout: 'Only use the wire instructions shown here. Doorly will never email you wire instructions.',
  // Closing
  uploadHoi: 'Upload your insurance declarations page',
  uploadDeclarations: 'Upload declarations page',
  getQuote: 'Need insurance? Get a quote',
  signCd: 'Sign Closing Disclosure',
  cdLine: 'You’ll receive it at least 3 business days before closing.',
  setUpUtilities: 'Set up utilities',
  // Keys in hand
  keysHeadline: 'Congratulations, your home is yours!',
  keysLine: 'Your closing documents are in Documents.',
  keysButton: 'View your closing documents',
  firstPayment: (amount, ymd) => `Your first payment of ${formatUsd(amount)} is due ${formatMonD(ymd)}. We’ll remind you before it’s due.`,
  storyConsent: 'Doorly may share my story publicly (first name and city only)',
  // Global
  next: 'Next',
  messageDoorly: 'Message Doorly',
  callSupport: 'Call support',
  simulatorSub: 'Try a different price or down payment',
};

// ── 🔑 NEW WORDING THE SPEC DOES NOT GIVE: BUILT, SWITCHED OFF (dashboard stages spec, 2026-11-03) ──
// Every string here is unapproved. While this is false the element is not shown (or today's wording stays): see
// .scratch/reports/dashboard-build/REPORT.md "Unapproved strings". Turning it on is this one line, after Hannah approves.
export const DASHBOARD_NEW_WORDING_APPROVED = false;
export const UNAPPROVED_COPY = Object.freeze({
  offerSentNoAddress: 'Doorly’s offer has been sent',
  offerAcceptedNoAddress: 'Doorly’s offer was accepted',
  inspectionNote: (ymd) => `Want your own inspection? Book it at your cost within the option period. Send any seller credit request by ${formatMonD(ymd)}.`,
  hoiLine: 'Your policy must name Go Doorly, LLC ISAOA/ATIMA as mortgagee, cover at least your loan amount or full replacement cost, and start by your closing date.',
});

// ── 🔑 ROUND 2 WORDING (Jesse/Hannah 2026-11-04) — given verbatim in the round-2 request, but NEW, so OFF until Hannah
// approves it (a sibling of DASHBOARD_NEW_WORDING_APPROVED so each round can be approved on its own). While false:
// "Call support" stays a tel: link everywhere; no "What happens next" card; the under-contract hero has no line before the
// contract is out; the locked down payment keeps its own description; no "Your mortgage" card, no "Pay my mortgage" item.
export const DASHBOARD_ROUND2_WORDING_APPROVED = true;   // 🔑 APPROVED (Hannah via Jesse, 2026-11-04): all 14, #1 and #5 as revised
export const ROUND2_COPY = Object.freeze({
  callNumber: (display) => `Call us at ${display}`,
  nextTitle: 'What happens next',
  nextSteps: Object.freeze(['We prepare Doorly’s offer', 'We send it to the seller’s agent', 'We let you know how the seller responds']),
  nextLine: 'If adjustments are needed to stay competitive, we’ll guide you through your options.',
  preparingContract: 'We’re preparing your purchase contract. You’ll be able to sign it here soon.',
  downPaymentLocked: 'Available after you sign your contract',
  mortgageTitle: 'Your mortgage',
  mortgagePending: 'We’ll send your first payment details before it’s due.',
  mortgageAmount: 'Payment',
  mortgageDue: 'Next due',
  howToPay: 'How to pay',
  payMyMortgage: 'Pay my mortgage',
});

/** The street part of a full address ("329 Journey Way, Austin, TX 78745" → "329 Journey Way"). Pure. */
export function streetOf(address) {
  const a = String(address || '').trim();
  if (!a) return '';
  return a.split(',')[0].trim();
}

/** The option period's two-days-before seller-credit cutoff, 'YYYY-MM-DD' (calendar arithmetic, never a zone shift). */
export function creditCutoffYmd(ymd) {
  const p = ymdParts(ymd);
  if (!p) return '';
  const d = new Date(Date.UTC(p.y, p.mo - 1, p.d) - 2 * 86400000);
  return d.toISOString().slice(0, 10);
}

/** Any date-ish value → 'YYYY-MM-DD' for the "Nov 9" style: a stored DATE as stored; a timestamp on its local day. */
export function ymdOf(v) {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T00:00:00(?:\.000)?Z$)/.exec(String(v));
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Any date-ish value → "Nov 9". */
export const monD = (v) => formatMonD(ymdOf(v));

/** Show the option-period line? During the option period, until 5:00 p.m. on its end date (the server's instant). */
export function showOptionEnds(status, now = Date.now()) {
  const j = status && status.journey;
  if (!status || status.loan_stage !== 'option_period' || !j || !j.option_period_end) return false;
  if (!j.option_period_ends_at) return true;
  // 🔑 Through 5:00 p.m. itself (>=), the same instant the server's mayTerminate allows (utils/optionPeriodOutcome).
  return new Date(j.option_period_ends_at).getTime() >= now;
}

// ── 🔑 THE COLOUR RULE FOR CHECKLISTS (Hannah, 2026-11-02): white; light blue for the ONE item waiting on the borrower;
// a green check for done; red ONLY for overdue. No lime, no amber, no red tints otherwise. Primary buttons are the app's
// primary (Doorly blue, white text) — never lime.
export const CHECK_COLOURS = Object.freeze({
  card: { background: '#fff', border: '1px solid #e5e7eb' },
  waiting: { background: '#eff3fe', border: '1px solid #bfdbfe' },   // Step5AskCard ASK_BOX
  note: { background: '#fff', border: '1px solid #e5e7eb', color: '#000' },
  doneTick: '#16a34a',
  overdue: '#b91c1c',
  primary: { background: '#3a6ad9', color: '#fff' },
});

/** The circle and the due label of a checklist item. overdue = past due and not done. */
export function checklistItemColours({ done = false, overdue = false } = {}) {
  if (done) return { circleBg: CHECK_COLOURS.doneTick, circleBorder: CHECK_COLOURS.doneTick, tick: '#fff', label: null };
  if (overdue) return { circleBg: '#fff', circleBorder: CHECK_COLOURS.overdue, tick: null, label: CHECK_COLOURS.overdue };
  return { circleBg: '#fff', circleBorder: '#d1d5db', tick: null, label: '#374151' };
}

/** The index of the one item waiting on the borrower: the first not done, not optional, that they can act on, else -1. */
export function waitingIndex(items) {
  return (items || []).findIndex((it) => it && it.kind !== 'notice' && !it.done && !it.optional && it.actionable !== false);
}

/** Is a 'YYYY-MM-DD' due date past (the day after it has begun)? */
export function isOverdue(ymd, now = new Date()) {
  const p = ymdParts(ymd);
  if (!p) return false;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Date.UTC(p.y, p.mo - 1, p.d) < today;
}

// ── 🔑 THE QUICK ACTION CARDS, BACK BELOW THE MAIN CARD (Hannah's production review, 2026-11-03 item 2) ──
// Restored from the pre-redesign dashboard (git fa57ab66^ pages/Dashboard.js "Quick Actions"), same destinations, same
// stage rule, restyled as the page's white cards:
//   View Documents   → /documents (view only — the label says so; uploads live on the guided steps)
//   SmartEstimate    → the simulator (SmartEstimatePreview in the dashboard's modal) — NEVER the signed PDF; the hero's
//                      "View your SmartEstimate" is the signed PDF
//   Browse Homes     → /homes; the Homes Locked dialog while the nav's Homes is locked (constants/navBadges homesLocked —
//                      the old card used an allow-list that locked under_contract / offer_accepted borrowers out)
//   What's Next      → the What's Next dialog (its stage checklist, unchanged)
// From option_period, closing and closed the middle two are Schedule (/schedule) and View Alerts (/alerts), as before.
// 🔑 The simulator card's wording: APPROVED by Hannah 2026-11-03 ("SmartEstimate Simulator" / "See how a different
// purchase price or down payment amount changes your numbers") — replaces "SmartEstimate" / "See how your loan works".
// 🔑 FROM THE OFFER STEP ON (dashboard stages spec B6, 2026-11-03): the existing set View Documents · Schedule · View
// Alerts · What's Next — switched earlier than before (it began at option_period). Home search keeps its set; Keys in hand
// has NO quick action cards (an empty list).
export const QUICK_ACTION_SCHEDULE_STAGES = ['offer_requested', 'offer_submitted', 'offer_accepted', 'option_period', 'under_contract',
  'loan_docs', 'closing_disclosures', 'final_review', 'closing', 'clear_to_close'];
export const QUICK_ACTION_NONE_STAGES = ['closed', 'funded'];

export function quickActionsFor(stage) {
  const docs = { key: 'documents', to: '/documents', label: 'View Documents', sub: 'Manage your required files' };
  const next = { key: 'whats-next', action: 'whats-next', label: 'What\'s Next', sub: 'See your next steps' };
  if (QUICK_ACTION_NONE_STAGES.includes(String(stage || ''))) return [];
  if (QUICK_ACTION_SCHEDULE_STAGES.includes(String(stage || ''))) {
    return [docs,
      { key: 'schedule', to: '/schedule', label: 'Schedule', sub: 'View your key dates' },
      { key: 'alerts', to: '/alerts', label: 'View Alerts', sub: 'Check your latest updates' },
      next];
  }
  const simulator = { key: 'smartestimate', action: 'simulator', label: 'SmartEstimate Simulator',
    sub: JOURNEY_COPY.simulatorSub };   // 🔑 shortened (dashboard stages spec B7, 2026-11-03)
  return [docs, simulator, { key: 'browse', action: 'browse', to: '/homes', label: 'Browse Homes', sub: 'View available listings' }, next];
}

// ── 🔑 THE FIVE STEP NAMES UNDER THE BAR (Hannah's production review, 2026-11-03 item 4) — desktop and tablet (sm and
// up); the current one bold. A phone keeps the bar and "Step X of 5" (JOURNEY_COPY.stepLine, already approved).
export function journeyStepNameRow(step) {
  return JOURNEY_STEP_NAMES.map((name, i) => ({ name, step: i + 1, current: i + 1 === Number(step) }));
}

// ── 🔑 THE DASHBOARD GREETING — OPTIONS FOR HANNAH (2026-11-03), ONE CONSTANT ──
// 'current' (shipped): the small "Welcome back, [first name]" line above "Step X of 5". The others are built for her
// pick (.scratch/reports/dashboard-greeting); choosing one is this one line. The step label, the step title and the bar
// stay as approved in every option.
//   'a' — no line above; the guided flow's slim white welcome card (components/guided/GuidedWelcomeCard) BELOW the bar:
//         "Welcome back, [first name]."
//   'b' — one small line above the step title: "Welcome back, [first name] · Step X of 5" (the separate "Step X of 5"
//         label is not shown, so it is never said twice).
//   'c' — no greeting line; the first name leads the main card's headline: "[First name], you’re approved up to $X"
//         (each stage's real headline, its first letter lower-cased after the name).
//   'd' — the step label row carries the greeting at its right ("Step X of 5" left, "Welcome back, [first name]" right):
//         no extra line, the step title stays the heading.
// No first name → "Welcome back" (a: "Welcome back."); c: the headline unchanged.
//   'heading' — 🔑 APPROVED (Hannah 2026-11-03, the final dashboard mockups): the small "Step X of 5" label, then
//         "Welcome back, [first name]" as the page heading at title size (it replaces the large step title), then the bar
//         with the step names under it from 640px up (the current one bold). On a phone the names are hidden and the label
//         reads "Step X of 5 · [step name]".
export const DASHBOARD_GREETING = 'heading';
export const DASHBOARD_GREETING_OPTIONS = ['current', 'a', 'b', 'c', 'd', 'heading'];

/** The main card's headline with the first name in front (option 'c'); unchanged without a name. */
export function headlineWithName(first, headline) {
  const h = String(headline || '');
  if (!first || !h) return h;
  return `${first}, ${h.charAt(0).toLowerCase()}${h.slice(1)}`;
}
